import React, { ChangeEvent, useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Button,
  CardMedia,
  Input,
  TextField,
  CircularProgress,
  Box,
} from "@mui/material";
import PhoneInput from "react-phone-input-2";
import CloseIcon from "@mui/icons-material/Close";
import "react-phone-input-2/lib/bootstrap.css";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import CommonBreadcrumb from "../../components/CommonBreadcrumb";
import { toast } from "react-toastify";
import { useUploadMediaMutation } from "../../service/features/api";
import { useAddShowroomMutation, useGetShowroomByIdQuery, useUpdateShowroomMutation } from "../../service/showroom/api";
import { useUploadBlogImagesMutation } from "../../service/blogs/api";

const AddEditDealer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const isAddUser = !id;

  const { data: showroomResponse, isLoading: isFetching } = useGetShowroomByIdQuery(id, { skip: isAddUser });
  const [updateShowroom, { isLoading: isUpdating }] = useUpdateShowroomMutation();

  const [showroomImages, setShowroomImages] = useState<string[]>([]);
  const [uploadMedia, { isLoading: isUploading }] = useUploadMediaMutation();
  const [logo, setLogo] = useState<string>("");
  const [descriptionEn, setDescriptionEn] = useState("");
  const [descriptionAr, setDescriptionAr] = useState("");
  const [documents, setDocuments] = useState<{ name: string; url: string }[]>([{ name: "", url: "" }]);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [openingTime, setOpeningTime] = useState("");
  const [closingTime, setClosingTime] = useState("");
  const [errors, setErrors] = useState<any>({});

  const [addShowroom, { isLoading: isAdding }] = useAddShowroomMutation();
  const [uploadBlogImages, { isLoading: isUploadingBlogImages }] = useUploadBlogImagesMutation();

  const validate = () => {
    const newErrors: any = {};

    if (!name || name.trim().length < 2) {
      newErrors.name = "Showroom Name must be at least 2 characters";
    }

    if (!address || address.trim().length < 5) {
      newErrors.address = "Address must be at least 5 characters";
    }

    if (!email) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {  // NOSONAR
      newErrors.email = "Invalid email address";
    }

    if (!phone || phone.length < 6) {
      newErrors.phone = "Phone number must be at least 6 characters";
    }

    if (!whatsappNumber || whatsappNumber.length < 6) {
      newErrors.whatsappNumber = "Whatsapp number must be at least 6 characters";
    }

    if (!openingTime) {
      newErrors.openingTime = "Opening time is required";
    }

    if (!closingTime) {
      newErrors.closingTime = "Closing time is required";
    }

    if (openingTime && closingTime) {
      const opening = openingTime.split(":").map(Number);
      const closing = closingTime.split(":").map(Number);
      const openingMinutes = opening[0] * 60 + opening[1];
      const closingMinutes = closing[0] * 60 + closing[1];

      if (closingMinutes <= openingMinutes) {
        newErrors.closingTime = "Closing time must be after opening time";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useEffect(() => {
    if (showroomResponse?.data && !isAddUser) {
      const data = showroomResponse.data;
      setName(data.name || "");
      setAddress(data.address || "");
      setLogo(data.logo || "");
      setOpeningTime(data.openingTime || "");
      setClosingTime(data.closingTime || "");
      setEmail(data.email || "");
      setPhone(data.phone || "");
      setWhatsappNumber(data.whatsappNumber || "");
      
      const imgs = data.images?.map((img: any) => img.url) || [];
      setShowroomImages(imgs);

      const docs = data.documents?.map((doc: any) => ({
        name: doc.documentType || "",
        url: doc.documentUrl || ""
      })) || [{ name: "", url: "" }];
      setDocuments(docs.length > 0 ? docs : [{ name: "", url: "" }]);

      setDescriptionEn(data.description?.en || "");
      setDescriptionAr(data.description?.ar || "");
    }
  }, [showroomResponse, isAddUser]);

  const handleSubmit = async () => {
    if (!validate()) {
      toast.error("Please fix the validation errors");
      return;
    }
    try {
      const payload = {
        name,
        address,
        logo,
        openingTime,
        closingTime,
        email,
        phone,
        whatsappNumber,
        images: showroomImages.map(url => ({ url })),
        documents: documents
          .filter(doc => doc.name && doc.url)
          .map(doc => ({
            businessName: name || "Showroom", 
            documentType: doc.name,
            documentUrl: doc.url,
          })),
        description: {
          en: descriptionEn,
          ar: descriptionAr,
        },
        isActive: isAddUser ? true : (showroomResponse?.data?.isActive ?? false),
      };

      if (isAddUser) {
        await addShowroom(payload).unwrap();
        toast.success("SHOWROOM_CREATED_SUCCESSFULLY");
      } else {
        await updateShowroom({ id, ...payload }).unwrap();
        toast.success("SHOWROOM_UPDATED_SUCCESSFULLY");
      }
      navigate("/manage-dealers");
    } catch (err: any) {
      toast.error(err?.data?.message || "Failed to create showroom");
    }
  };

  return (
    <>
      {/* PAGE TITLE */}
      <CommonBreadcrumb
        title={`${isAddUser ? "إضافة / Add" : "تعديل / Edit"} المعرض / Showroom`}
        breadcrumbItems={[
          { label: "لوحة المعلومات / Dashboard", onClick: () => navigate("/dashboard") },
          {
            label: "إدارة المعارض / Showrooms Management",
            onClick: () => navigate("/manage-dealers"),
          },
          { label: `${isAddUser ? "إضافة / Add" : "تعديل / Edit"} المعرض / Showroom` },
        ]}
      />

      {isFetching ? (
        <Box display="flex" justifyContent="center" padding="50px">
          <CircularProgress />
        </Box>
      ) : (
      <div className="form">
        <div className="cards">
          <div className="table_header">
            <h2>معلومات المعرض / Showroom Information</h2>
          </div>
          <div className="gap_p">
            {/* Dealer/Seller Logo — at the very top */}
            <div className="control_group w_33">
              <label>شعار المعرض / Showroom Logo</label>
              {logo ? (
                <div className="upload_image no_radius v2">
                  <div className="upload_image_holder">
                    <figure>
                      <CardMedia component="img" image={logo} />
                    </figure>
                    <CloseIcon
                      className="remove_img"
                      onClick={() => setLogo("")}
                    />
                  </div>
                </div>
              ) : (
                <label 
                  className="upload_image no_radius v2"
                  style={{ cursor: isUploading ? "not-allowed" : "pointer" }}
                >
                  <Input
                    sx={{ display: "none" }}
                    type="file"
                    inputProps={{ accept: "image/*" }}
                    disabled={isUploading}
                    onChange={async (event: ChangeEvent<HTMLInputElement>) => {
                      const files = event.target.files;
                      if (files && files[0]) {
                        const file = files[0];
                        const formData = new FormData();
                        formData.append("file", file);
                        try {
                          const res = await uploadMedia(formData).unwrap();
                          const imageUrl = res?.data?.url || res?.url || res?.data;
                          if (imageUrl) {
                            setLogo(imageUrl);
                            toast.success("Logo uploaded");
                          }
                        } catch (err: any) {
                          toast.error(err?.data?.message || "Upload failed");
                        }
                        event.target.value = "";
                      }
                    }}
                  />
                  <span className="upload_image_holder">
                    {isUploading ? (
                      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", width: "100%" }}>
                        <CircularProgress size={24} />
                      </div>
                    ) : (
                      <figure className="add_img">
                        <img src="/static/images/placeholder.png" alt="" />
                        <span className="upload_img_icon">
                          <img src="/static/images/edit2_icon.svg" alt="" />
                        </span>
                      </figure>
                    )}
                  </span>
                </label>
              )}
            </div>

            {/* Showroom Images */}
            <div className="control_group w_100">
              <label>تحميل الصور / Upload Images <small>- {showroomImages.length} of 20 Images (JPG, max. 10 MB per Image)</small></label>
              <div className="gap_p">
                {/* Upload Button — always first/leftmost */}
                {showroomImages.length < 20 && (
                  <div className="w_25">
                    <label
                      className="upload_image no_radius v2"
                      style={{ cursor: (isUploading || isUploadingBlogImages) ? "not-allowed" : "pointer" }}
                      htmlFor="dealer-image-upload"
                    >
                      <Input
                        sx={{ display: "none" }}
                        id="dealer-image-upload"
                        type="file"
                        inputProps={{ accept: "image/*", multiple: true }}
                        disabled={isUploading || isUploadingBlogImages}
                        onChange={async (event: ChangeEvent<HTMLInputElement>) => {
                          const files = event.target.files;
                          if (files && files.length > 0) {
                            if (showroomImages.length + files.length > 20) {
                              toast.error("You can upload a maximum of 20 images");
                              event.target.value = "";
                              return;
                            }
                            const formData = new FormData();
                            Array.from(files).forEach(file => {
                              formData.append("files", file);
                            });
                            try {
                              const res = await uploadBlogImages(formData).unwrap();
                              const imageUrls = res?.data || [];
                              if (imageUrls.length > 0) {
                                setShowroomImages([...showroomImages, ...imageUrls]);
                                toast.success(`${imageUrls.length} image(s) uploaded`);
                              }
                            } catch (err: any) {
                              toast.error(err?.data?.message || "Upload failed");
                            }
                            event.target.value = "";
                          }
                        }}
                      />
                      <span className="upload_image_holder">
                        {isUploadingBlogImages ? (
                          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%", width: "100%" }}>
                            <CircularProgress size={24} />
                          </div>
                        ) : (
                          <figure className="add_img">
                            <img src="/static/images/placeholder.png" alt="" />
                            <span className="upload_img_icon">
                              <img src="/static/images/edit2_icon.svg" alt="" />
                            </span>
                          </figure>
                        )}
                      </span>
                    </label>
                  </div>
                )}

                {/* Image Previews */}
                {showroomImages.map((img, i) => (
                  <div className="w_25" key={i}>
                    <div className="upload_image no_radius v2">
                      <div className="upload_image_holder">
                        <figure>
                          <CardMedia component="img" image={img} />
                        </figure>
                        <CloseIcon
                          className="remove_img"
                          onClick={() => {
                            const newImages = showroomImages.filter((_, index) => index !== i);
                            setShowroomImages(newImages);
                          }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Showroom Name */}
            <div className="control_group w_50">
              <label>اسم المعرض / Showroom Name</label>
              <TextField 
                placeholder="أدخل اسم المعرض / Enter Showroom Name" 
                hiddenLabel 
                fullWidth 
                value={name} 
                onChange={e => {
                  setName(e.target.value);
                  if (errors.name) setErrors({ ...errors, name: "" });
                }} 
                error={!!errors.name}
                helperText={errors.name}
              />
            </div>

            {/* Registered Address */}
            <div className="control_group w_100">
              <label>العنوان المسجل للمعرض / Showroom Registered Address</label>
              <TextField
                placeholder="أدخل العنوان المسجل / Enter Registered Address"
                hiddenLabel
                fullWidth
                multiline
                rows={3}
                value={address}
                onChange={e => {
                  setAddress(e.target.value);
                  if (errors.address) setErrors({ ...errors, address: "" });
                }}
                error={!!errors.address}
                helperText={errors.address}
              />
            </div>

            {/* Email */}
            <div className="control_group w_33">
              <label>البريد الإلكتروني / Email</label>
              <TextField 
                type="email" 
                placeholder="أدخل البريد الإلكتروني / Enter Email" 
                hiddenLabel 
                fullWidth 
                value={email} 
                onChange={e => {
                  setEmail(e.target.value);
                  if (errors.email) setErrors({ ...errors, email: "" });
                }} 
                error={!!errors.email}
                helperText={errors.email}
              />
            </div>

            {/* Phone */}
            <div className="control_group w_33">
              <label>رقم الهاتف / Phone Number</label>
              <PhoneInput
                country="in"
                placeholder="أدخل رقم الهاتف / Enter Phone Number"
                inputStyle={{ width: "100%", borderColor: errors.phone ? "#d32f2f" : "#E0E0E0" }}
                value={phone}
                onChange={val => {
                  setPhone(val);
                  if (errors.phone) setErrors({ ...errors, phone: "" });
                }}
              />
              {errors.phone && <p style={{ color: "#d32f2f", fontSize: "0.75rem", margin: "4px 14px 0" }}>{errors.phone}</p>}
            </div>

            {/* Whatsapp */}
            <div className="control_group w_33">
              <label>رقم الواتساب / Whatsapp Number</label>
              <PhoneInput
                country="in"
                placeholder="أدخل رقم الواتساب / Enter Whatsapp Number"
                inputStyle={{ width: "100%", borderColor: errors.whatsappNumber ? "#d32f2f" : "#E0E0E0" }}
                value={whatsappNumber}
                onChange={val => {
                  setWhatsappNumber(val);
                  if (errors.whatsappNumber) setErrors({ ...errors, whatsappNumber: "" });
                }}
              />
              {errors.whatsappNumber && <p style={{ color: "#d32f2f", fontSize: "0.75rem", margin: "4px 14px 0" }}>{errors.whatsappNumber}</p>}
            </div>

            <div className="control_group w_50">
              <div className="gap_p">
                {/* Opening Time */}
                <div className="control_group w_50">
                  <label>وقت الفتح / Opening Time</label>
                  <TextField 
                    type="time" 
                    hiddenLabel 
                    fullWidth 
                    value={openingTime} 
                    onChange={e => {
                      setOpeningTime(e.target.value);
                      if (errors.openingTime) setErrors({ ...errors, openingTime: "" });
                    }} 
                    error={!!errors.openingTime}
                    helperText={errors.openingTime}
                  />
                </div>

                {/* Closing Time */}
                <div className="control_group w_50">
                  <label>وقت الإغلاق / Closing Time</label>
                  <TextField 
                    type="time" 
                    hiddenLabel 
                    fullWidth 
                    value={closingTime} 
                    onChange={e => {
                      setClosingTime(e.target.value);
                      if (errors.closingTime) setErrors({ ...errors, closingTime: "" });
                    }} 
                    error={!!errors.closingTime}
                    helperText={errors.closingTime}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="cards">
          <div className="table_header">
            <h2>تحميل المستندات / Upload Documents</h2>
          </div>
          <div className="gap_p">
            {documents.map((doc, index) => (
              <div 
                className="w_100" 
                key={index} 
                style={{ 
                  display: "flex", 
                  gap: "20px", 
                  alignItems: "flex-end", 
                  marginBottom: "15px" 
                }}
              >
                <div className="control_group" style={{ flex: 1, margin: 0 }}>
                  <label>اسم المستند / Document Name</label>
                  <TextField
                    placeholder="أدخل اسم المستند / Enter Document Name"
                    hiddenLabel
                    fullWidth
                    value={doc.name}
                    onChange={(e) => {
                      const newDocs = [...documents];
                      newDocs[index].name = e.target.value;
                      setDocuments(newDocs);
                    }}
                  />
                </div>
                
                <div className="control_group" style={{ flex: 1, margin: 0 }}>
                  <label>تحميل المستند / Upload Document</label>
                  {doc.url ? (
                    <div className="upload_box" style={{ padding: "0 20px", height: "54px", minHeight: "auto", display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px solid #E0E0E0", borderRadius: "8px" }}>
                      <a href={doc.url} target="_blank" rel="noreferrer" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", textDecoration: "none", color: "#1976d2", maxWidth: "80%" }}>
                        {doc.url.split("/").pop() || "View Document"}
                      </a>
                      <CloseIcon 
                        style={{ cursor: "pointer", color: "#757575" }}
                        onClick={() => {
                          const newDocs = [...documents];
                          newDocs[index].url = "";
                          setDocuments(newDocs);
                        }} 
                      />
                    </div>
                  ) : (
                    <div className="upload_box" style={{ padding: "0", height: "54px", minHeight: "auto", border: "1px dashed #E0E0E0", borderRadius: "8px" }}>
                      <label 
                        className="upload_action" 
                        style={{ height: "100%", margin: 0, padding: 0, flexDirection: "row", gap: "10px", justifyContent: "center", cursor: isUploading ? "wait" : "pointer" }}
                      >
                        <input 
                          type="file" 
                          disabled={isUploading}
                          onChange={async (event: ChangeEvent<HTMLInputElement>) => {
                            const files = event.target.files;
                            if (files && files[0]) {
                              const formData = new FormData();
                              formData.append("file", files[0]);
                              try {
                                const res = await uploadMedia(formData).unwrap();
                                const uploadedUrl = res?.data?.url || res?.url || res?.data;
                                if (uploadedUrl) {
                                  const newDocs = [...documents];
                                  newDocs[index].url = uploadedUrl;
                                  setDocuments(newDocs);
                                  toast.success("Document uploaded");
                                }
                              } catch (err: any) {
                                toast.error(err?.data?.message || "Upload failed");
                              }
                            }
                            event.target.value = "";
                          }}
                        />
                        {isUploading ? (
                          <CircularProgress size={20} />
                        ) : (
                          <>
                            <FileUploadOutlinedIcon />
                            <p style={{ margin: 0 }}>تحميل ملف / Upload File</p>
                          </>
                        )}
                      </label>
                    </div>
                  )}
                </div>

                {documents.length > 1 && (
                  <div style={{ display: "flex", height: "54px", alignItems: "center", paddingBottom: "2px" }}>
                    <Button 
                      color="error"
                      variant="outlined"
                      onClick={() => {
                        setDocuments(documents.filter((_, i) => i !== index));
                      }}
                      style={{ textTransform: "capitalize", height: "100%" }}
                    >
                      إزالة / Remove
                    </Button>
                  </div>
                )}
              </div>
            ))}

            <div className="control_group w_100" style={{ margin: 0 }}>
              <Button 
                variant="outlined" 
                onClick={() => setDocuments([...documents, { name: "", url: "" }])}
                style={{ textTransform: "capitalize" }}
              >
                + إضافة مستند آخر / Add Another Document
              </Button>
            </div>
          </div>
        </div>

        <div className="cards">
          <div className="table_header">
            <h2>مزيد من التفاصيل / Further Details</h2>
          </div>
          <div className="gap_p">
            {/* Description — English */}
            <div className="control_group w_50">
              <label>الوصف (الإنجليزية) / Description (English)</label>
              <TextField
                placeholder="أدخل الوصف باللغة الإنجليزية / Enter Description in English"
                hiddenLabel
                fullWidth
                multiline
                rows={4}
                value={descriptionEn}
                onChange={(e) => {
                  let val = e.target.value;
                  if (val.length === 1 && val === " ") val = "";
                  if (val.length <= 800) setDescriptionEn(val);
                }}
                inputProps={{ maxLength: 800 }}
              />
            </div>

            {/* Description — Arabic */}
            <div className="control_group w_50">
              <label>الوصف (العربية) / Description (Arabic)</label>
              <TextField
                placeholder="أدخل الوصف باللغة العربية / Enter Description in Arabic"
                hiddenLabel
                fullWidth
                multiline
                rows={4}
                value={descriptionAr}
                onChange={(e) => {
                  let val = e.target.value;
                  if (val.length === 1 && val === " ") val = "";
                  if (val.length <= 800) setDescriptionAr(val);
                }}
                inputProps={{ maxLength: 800, dir: "rtl" }}
              />
            </div>
          </div>
        </div>

        {/* SAVE BUTTON */}
        <div className="form_btn">
          <Button className="btn btn_primary" onClick={handleSubmit} disabled={isAdding || isUpdating}>
            {isAdding || isUpdating ? "Saving..." : "حفظ / Save"}
          </Button>
        </div>
      </div>
      )}
    </>
  );
};

export default AddEditDealer;